/**
 * The four sentinels, on the page, **before** anybody opens the file.
 *
 * D-22 is the reason this is a panel and not a footnote: a withheld overtime figure
 * shipped as **"0 giờ"**, and the person who reads it has no way to know. The file's
 * own legend names only the sentinels that file used — which is right for the file,
 * because a legend reciting absences the reader will never meet is a legend nobody
 * reads — but the *requesting* screen does not know which ones a file will contain,
 * so it names all four and says so.
 *
 * The point of putting it here at all is the moment of use. An HR administrator opens
 * a CSV in Excel, meets `#WITHHELD_PRIVATE` in a column of numbers, and has to decide
 * what to do with it. Without this panel the obvious thing to do is type `0`, and
 * that single keystroke destroys the distinction four upstream tasks were built to
 * preserve — Task 36's uncomputed hour, Task 35's private activity, Task 27's
 * unattestable day, and every "no shift assigned" row.
 *
 * There are **no controls here and specifically no disabled ones**. It is a
 * reference.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { ABSENCE_KINDS, ABSENCE_TEXT } from '../../reports/figure';
import type { MessageKey } from '../../i18n/messages';

export function AbsenceLegend(): ReactNode {
  const { t } = useI18n();
  return (
    <section
      className="wp-section"
      aria-labelledby="k8-absence-heading"
      data-testid="k8-absence-legend"
    >
      <h2 className="wp-section__title" id="k8-absence-heading">
        {t('k8.reports.absence.title')}
      </h2>
      <p>{t('k8.reports.absence.body')}</p>
      <dl className="wp-keys">
        {ABSENCE_KINDS.map((kind) => (
          <div className="wp-keys__item" key={kind} data-testid={`k8-absence-${kind}`}>
            <dt>
              <code className="wp-mono">{ABSENCE_TEXT[kind]}</code>
            </dt>
            <dd className="wp-small">{t(`k8.reports.absence.${kind}` as MessageKey)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
